/**
 * Thin typed wrapper around localStorage with JSON serialization.
 *
 * `get` returns the parsed value, or `fallback` when the key is absent
 * or the stored payload is unparseable. A corrupt payload — most
 * frequently the literal string `"undefined"` (the wire shape a
 * pre-`b08dd6ce` `JSON.stringify(undefined)` boot left behind) — is
 * removed on read so subsequent boots see a clean substrate instead of
 * the same corruption repeatedly hitting the fallback branch. Without
 * this self-healing the corruption accumulates silently: every later
 * `set` overwrites the key, but if nothing writes it again (e.g., a
 * never-set server-session expiry sibling) the literal value sticks
 * across reloads and the per-tier predicates (`isServerSessionExpired`
 * / `isDraftExpired`) compute against `null` while localStorage still
 * holds the bad payload (AJ-W1-ζ Path ζ-A).
 */
export const typedStorage = {
    get<T>(key: string, fallback: T): T {
        if (typeof localStorage === "undefined") return fallback;
        const raw = localStorage.getItem(key);
        if (raw === null) return fallback;
        try {
            return JSON.parse(raw) as T;
        } catch {
            // Self-heal corrupt payload: remove the key so the next boot
            // doesn't keep tripping the parse fallback path. The
            // observable behaviour stays identical (`fallback` returns)
            // and the substrate ends up matching the in-memory ref.
            try {
                localStorage.removeItem(key);
            } catch {
                // Storage quota / disabled — fall through; the fallback
                // is still correct, just without the cleanup.
            }
            return fallback;
        }
    },

    set<T>(key: string, value: T): void {
        if (typeof localStorage === "undefined") return;
        localStorage.setItem(key, JSON.stringify(value));
    },

    remove(key: string): void {
        if (typeof localStorage === "undefined") return;
        localStorage.removeItem(key);
    },

    /** Remove all keys that start with the given prefix. */
    clearPrefix(prefix: string): void {
        if (typeof localStorage === "undefined") return;
        const toRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key?.startsWith(prefix)) toRemove.push(key);
        }
        for (const key of toRemove) localStorage.removeItem(key);
    },
};
